import React, { Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './app.css';

import { useAuthStore } from '@/stores/auth';
import { InstallPrompt } from '@/components/ui/InstallPrompt';

const FounderDashboardPage = React.lazy(() => import('@/pages/FounderDashboardPage').then(m => ({ default: m.FounderDashboardPage })));

// Lightweight page view tracker
function PageTracker() {
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  useEffect(() => {
    // Track page view
    const base = import.meta.env.PROD ? 'https://edgerelay-api.ghwmelite.workers.dev' : '';
    fetch(`${base}/v1/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventType: 'page_view', page: location.pathname, userId: user?.id }),
    }).catch(() => {});

    // Update canonical URL dynamically (fixes duplicate content issues)
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = `https://trademetricspro.com${location.pathname}`;
  }, [location.pathname]);
  return null;
}
import { AppLayout } from '@/components/layout/AppLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';

// Lazy-load ALL pages — reduces initial bundle from one monolithic chunk to per-route chunks
const LandingPage = React.lazy(() => import('@/pages/LandingPage').then(m => ({ default: m.LandingPage })));
const DashboardPage = React.lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const AccountsPage = React.lazy(() => import('@/pages/AccountsPage').then(m => ({ default: m.AccountsPage })));
const SignalLogPage = React.lazy(() => import('@/pages/SignalLogPage').then(m => ({ default: m.SignalLogPage })));
const BillingPage = React.lazy(() => import('@/pages/BillingPage').then(m => ({ default: m.BillingPage })));
const BillingCallbackPage = React.lazy(() => import('@/pages/BillingCallbackPage').then(m => ({ default: m.BillingCallbackPage })));
const DownloadsPage = React.lazy(() => import('@/pages/DownloadsPage').then(m => ({ default: m.DownloadsPage })));
const AnalyticsPage = React.lazy(() => import('@/pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const UsagePage = React.lazy(() => import('@/pages/UsagePage').then(m => ({ default: m.UsagePage })));
const LoginPage = React.lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import('@/pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const PropGuardSetupPage = React.lazy(() => import('@/pages/PropGuardSetupPage').then(m => ({ default: m.PropGuardSetupPage })));
const JournalPage = React.lazy(() => import('@/pages/JournalPage').then(m => ({ default: m.JournalPage })));
const JournalTradeDetailPage = React.lazy(() => import('@/pages/JournalTradeDetailPage').then(m => ({ default: m.JournalTradeDetailPage })));
const CommandCenterPage = React.lazy(() => import('@/pages/CommandCenterPage').then(m => ({ default: m.CommandCenterPage })));
const FirmDirectoryPage = React.lazy(() => import('@/pages/FirmDirectoryPage').then(m => ({ default: m.FirmDirectoryPage })));
const FirmDetailPage = React.lazy(() => import('@/pages/FirmDetailPage').then(m => ({ default: m.FirmDetailPage })));
const RiskDashboardPage = React.lazy(() => import('@/pages/RiskDashboardPage').then(m => ({ default: m.RiskDashboardPage })));
const ProviderSetupPage = React.lazy(() => import('@/pages/ProviderSetupPage').then(m => ({ default: m.ProviderSetupPage })));
const MarketplacePage = React.lazy(() => import('@/pages/MarketplacePage').then(m => ({ default: m.MarketplacePage })));
const StrategyHubPage = React.lazy(() => import('@/pages/StrategyHubPage').then(m => ({ default: m.StrategyHubPage })));
const PrivacyPolicyPage = React.lazy(() => import('@/pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = React.lazy(() => import('@/pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const AuthCallbackPage = React.lazy(() => import('@/pages/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })));
const DisciplinePage = React.lazy(() => import('@/pages/DisciplinePage').then(m => ({ default: m.DisciplinePage })));
const AdminPage = React.lazy(() => import('@/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const ReferralPage = React.lazy(() => import('@/pages/ReferralPage').then(m => ({ default: m.ReferralPage })));
const ReferralLandingPage = React.lazy(() => import('@/pages/ReferralLandingPage').then(m => ({ default: m.ReferralLandingPage })));
const BlogListPage = React.lazy(() => import('@/pages/BlogListPage').then(m => ({ default: m.BlogListPage })));
const BlogPostPage = React.lazy(() => import('@/pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const PropFirmChallengePage = React.lazy(() => import('@/pages/PropFirmChallengePage').then(m => ({ default: m.PropFirmChallengePage })));
const PropFirmHubPage = React.lazy(() => import('@/pages/PropFirmHubPage').then(m => ({ default: m.PropFirmHubPage })));
const CounselorPage = React.lazy(() => import('@/pages/CounselorPage').then(m => ({ default: m.CounselorPage })));
const MarketPulsePage = React.lazy(() => import('@/pages/MarketPulsePage').then(m => ({ default: m.MarketPulsePage })));
const PipCalculatorPage = React.lazy(() => import('@/pages/tools/PipCalculatorPage').then(m => ({ default: m.PipCalculatorPage })));
const PositionSizeCalculatorPage = React.lazy(() => import('@/pages/tools/PositionSizeCalculatorPage').then(m => ({ default: m.PositionSizeCalculatorPage })));
const RiskRewardCalculatorPage = React.lazy(() => import('@/pages/tools/RiskRewardCalculatorPage').then(m => ({ default: m.RiskRewardCalculatorPage })));
const ThreeStrategiesStudioPage = React.lazy(() => import('@/pages/ThreeStrategiesStudioPage').then(m => ({ default: m.ThreeStrategiesStudioPage })));
const TradeGoldPage = React.lazy(() => import('@/pages/TradeGoldPage').then(m => ({ default: m.TradeGoldPage })));
const TradeIndicesPage = React.lazy(() => import('@/pages/TradeIndicesPage').then(m => ({ default: m.TradeIndicesPage })));
const TradeOilPage = React.lazy(() => import('@/pages/TradeOilPage').then(m => ({ default: m.TradeOilPage })));
const TelegramMiniAppPage = React.lazy(() => import('@/pages/TelegramMiniAppPage').then(m => ({ default: m.TelegramMiniAppPage })));
const CommunityPage = React.lazy(() => import('@/pages/CommunityPage').then(m => ({ default: m.CommunityPage })));
const RangeBreakoutGuidePage = React.lazy(() => import('@/pages/RangeBreakoutGuidePage').then(m => ({ default: m.RangeBreakoutGuidePage })));
const AcademyPage = React.lazy(() => import('@/pages/AcademyPage').then(m => ({ default: m.AcademyPage })));
const AcademyLessonPage = React.lazy(() => import('@/pages/AcademyLessonPage').then(m => ({ default: m.AcademyLessonPage })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Show standalone public page if not logged in, redirect to in-app version if logged in */
function PublicOrAppRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  if (isAuthenticated) {
    // /firms/FTMO → /app/firms/FTMO
    return <Navigate to={`/app${location.pathname}`} replace />;
  }
  return <>{children}</>;
}

/** Capture ?ref= query param from URL and store in localStorage for registration. */
function ReferralCapture() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('referral_code', ref);
      // Clean URL without losing the page
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ReferralCapture />
      <InstallPrompt />
      <PageTracker />
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen bg-terminal-bg">
          <div className="flex items-center gap-3 text-terminal-muted">
            <span className="h-2 w-2 rounded-full bg-neon-cyan animate-pulse shadow-[0_0_6px_#00e5ff]" />
            Loading...
          </div>
        </div>
      }>
      <Routes>
        <Route path="/" element={<LandingPage />} />

        {/* Public legal pages */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />

        {/* Public firms pages — redirect to in-app version if logged in */}
        <Route path="/firms" element={<PublicOrAppRoute><FirmDirectoryPage /></PublicOrAppRoute>} />
        <Route path="/firms/:firmName" element={<PublicOrAppRoute><FirmDetailPage /></PublicOrAppRoute>} />
        <Route path="/marketplace" element={<PublicOrAppRoute><MarketplacePage /></PublicOrAppRoute>} />
        <Route path="/strategy-hub" element={<PublicOrAppRoute><StrategyHubPage /></PublicOrAppRoute>} />
        <Route path="/referral" element={<ReferralLandingPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/pass-prop-firm-challenge" element={<PropFirmChallengePage />} />
        <Route path="/markets" element={<MarketPulsePage />} />
        <Route path="/icc-studio" element={<Navigate to="/three-strategies" replace />} />
        <Route path="/three-strategies" element={<ThreeStrategiesStudioPage />} />
        <Route path="/tools/pip-calculator" element={<PipCalculatorPage />} />
        <Route path="/tools/position-size-calculator" element={<PositionSizeCalculatorPage />} />
        <Route path="/tools/risk-reward-calculator" element={<RiskRewardCalculatorPage />} />
        <Route path="/trade-gold" element={<TradeGoldPage />} />
        <Route path="/trade-indices" element={<TradeIndicesPage />} />
        <Route path="/trade-oil" element={<TradeOilPage />} />
        <Route path="/tg" element={<TelegramMiniAppPage />} />

        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        <Route element={<AuthLayout />}>
          <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
        </Route>

        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/command-center" element={<CommandCenterPage />} />
          <Route path="/risk" element={<RiskDashboardPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/signals" element={<SignalLogPage />} />
          <Route path="/journal" element={<JournalPage />} />
          <Route path="/journal/:accountId/:dealTicket" element={<JournalTradeDetailPage />} />
          <Route path="/app/firms" element={<FirmDirectoryPage />} />
          <Route path="/app/firms/:firmName" element={<FirmDetailPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/discipline" element={<DisciplinePage />} />
          <Route path="/usage" element={<UsagePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/billing/callback" element={<BillingCallbackPage />} />
          <Route path="/propguard/setup" element={<PropGuardSetupPage />} />
          <Route path="/app/prop-firms" element={<PropFirmHubPage />} />
          <Route path="/counselor" element={<CounselorPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/academy" element={<AcademyPage />} />
          <Route path="/academy/gold-range-guide" element={<RangeBreakoutGuidePage />} />
          <Route path="/academy/:lessonId" element={<AcademyLessonPage />} />
          <Route path="/academy/practice" element={<Navigate to="/academy/three-strategies" replace />} />
          <Route path="/academy/icc-studio" element={<Navigate to="/academy/three-strategies" replace />} />
          <Route path="/academy/three-strategies" element={<ThreeStrategiesStudioPage />} />
          <Route path="/simulator" element={<Navigate to="/academy/three-strategies" replace />} />
          <Route path="/provider/setup" element={<ProviderSetupPage />} />
          <Route path="/app/marketplace" element={<MarketplacePage />} />
          <Route path="/app/strategy-hub" element={<StrategyHubPage />} />
          <Route path="/referrals" element={<ReferralPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/founder" element={<FounderDashboardPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// ── Service Worker Registration ───────────────────────────────
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}
